import { inject, observer } from 'mobx-react';
import React from 'react';
import { createPortal } from 'react-dom';

import ActionCableComponent, {
  CableElementType
} from 'components/ActionCableComponent/ActionCableComponent';
import AppLayoutReduced from 'components/AppLayout/AppLayoutReduced';
import ErrorMessage from 'components/ErrorMessage';
import Icon from 'components/Icon';
import Columns from 'components/Layout/Columns';
import ColumnWrapper from 'components/Layout/ColumnWrapper';
import Loading from 'components/Loading';
import AiLoading from 'components/Loading/AiLoading';
import MainButton from 'components/MainButton';
import TabList from 'components/TabList';
import TabListButton from 'components/TabList/TabListButton';
import TabListMore, { OverflowTab } from 'components/TabList/TabListMore';
import ActionsOverlayContainer from 'containers/ActionsOverlayContainer';
import { ActionsStoreType } from 'models/ActionsStore';
import { ApplicationStoreType } from 'models/ApplicationStore';
import { BenchmarkModelType } from 'models/BenchmarkModel';
import { BriefingStoreType } from 'models/BriefingStore';
import { DataStoreType } from 'models/DataStore';
import { FireStoreType } from 'models/FireStore';
import { HypothesisModelType } from 'models/HypothesisModel';
import { OrganizationsStoreType } from 'models/OrganizationsStore';
import { PainpointModelType } from 'models/PainpointModel';
import { ClusterPainpoints, PainpointsStoreType } from 'models/PainpointsStore';
import { ProjectsStoreType } from 'models/ProjectsStore';
import { UserFireConfigStoreType } from 'models/UserFireConfigStore';
import {
  FormattedMessage,
  WrappedComponentProps,
  injectIntl
} from 'react-intl';
import BenchmarksListItem from 'screens/benchmarks/BenchmarksListScreen/BenchmarksListItem';
import HypothesesListItem from 'screens/hypotheses/HypothesesListScreen/HypothesesListItem';
import GapSizes from 'utils/constants/gap-sizes';
import ROLES from 'utils/constants/roles';
import { ROUTE_FIRE, ROUTE_PROJECT } from 'utils/constants/routes';
import { HistoryProps, idFromMatch } from 'utils/history';
import useForm, { FormType, handleFormError } from 'utils/hooks/useForm';
import ClusterWithPainpointComponent from './components/ClusterWithPainpointComponent';
import FireDetailTop from './components/fire_detail/FireDetailTop';
import FireBriefingComponent from './components/FireBriefingComponent';
import FireHeaderComponent from './components/FireHeaderComponent';
import FireProgressComponent from './components/FireProgressComponent';
import { AiModulesStoreType } from 'models/AiModulesStore';
import { ProjectAiModulesStoreType } from 'models/ProjectAiModulesStore';
import AiModulesSidebarComponent from './components/AiModulesSidebarComponent';
import ProjectAiModuleContent from './components/ProjectAiModuleContent';
import UserFireConfigBenchmarkSettings from './components/UserFireConfigBenchmarkSettings';
interface FireDetailScreenProps {
  fireStore: FireStoreType;
  dataStore: DataStoreType;
  projectsStore: ProjectsStoreType;
  userFireConfigStore: UserFireConfigStoreType;
  actionsStore: ActionsStoreType;
  form: FormType;
  painpointsStore: PainpointsStoreType;
  applicationStore: ApplicationStoreType;
  briefingStore: BriefingStoreType;
  organizationsStore: OrganizationsStoreType;
  aiModulesStore: AiModulesStoreType;
  projectAiModulesStore: ProjectAiModulesStoreType;
}

interface FireScreenOverlayState {
  showSettingsOverlay: boolean;
  activeTab: string;
  showExportOverlay: boolean;
  openShareTab: boolean;
  showShareOverlay: boolean;
  shareActiveTab: 'powerpoint' | 'pdf';
  isShareLoading: boolean;
  showConvertOverlay: boolean;
  briefingCompletionByGPTRequestCounter: number;
  isPowerpointLoading: boolean;
}

@inject(
  'fireStore',
  'dataStore',
  'userFireConfigStore',
  'projectsStore',
  'painpointsStore',
  'actionsStore',
  'applicationStore',
  'briefingStore',
  'organizationsStore',
  'aiModulesStore',
  'projectAiModulesStore'
)
@observer
class FireDetailScreen extends React.Component<
  FireDetailScreenProps & HistoryProps & WrappedComponentProps
> {
  state: FireScreenOverlayState = {
    showSettingsOverlay: false,
    activeTab: 'progress',
    showExportOverlay: false,
    openShareTab: false,
    showShareOverlay: false,
    shareActiveTab: 'powerpoint',
    isShareLoading: false,
    showConvertOverlay: false,
    briefingCompletionByGPTRequestCounter: 0,
    isPowerpointLoading: false
  };

  componentDidMount() {
    this.getFire();
    this.loadUserFireConfig();
    this.loadCustomers();

    const hash = window.location.hash.replace('#', '');
    if (
      hash &&
      (['briefing', 'painpoints', 'benchmarks', 'hypothesen'].includes(hash) ||
        hash.startsWith('ai_module_'))
    ) {
      this.setState({ activeTab: hash });
    }
  }

  loadCustomers = async () => {
    const { organizationsStore, dataStore } = this.props;
    const { currentOrganization } = dataStore;

    if (!currentOrganization) {
      return;
    }

    if (currentOrganization.is_agency) {
      await organizationsStore.getCustomers();
    } else {
      const agency_id = currentOrganization.agency_id;
      if (agency_id) {
        await organizationsStore.getCustomers(agency_id);
      }
    }
  };

  loadUserFireConfig = async () => {
    const { userFireConfigStore, form } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');
    if (!organizationId) {
      return;
    }
    await userFireConfigStore.getUserFireConfig(organizationId);

    form.setField(
      'target_group',
      userFireConfigStore.userFireConfig?.target_group
    );
    form.setField(
      'business_target',
      userFireConfigStore.userFireConfig?.business_target
    );
    form.setField('service', userFireConfigStore.userFireConfig?.service);
  };

  addPainpoint = async (newPainpoint: {
    question: string;
    cluster_id: number;
  }) => {
    const { painpointsStore, dataStore } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');
    const projectId = dataStore.project?.id;

    if (!projectId || !organizationId) {
      return;
    }

    const patch: any = {
      question: newPainpoint.question,
      cluster_id: newPainpoint.cluster_id,
      publish_state: 'active'
    };

    await painpointsStore.createPainpoint(
      patch,
      true,
      organizationId,
      projectId
    );
  };

  handleServiceSubmit = async () => {
    const { form, userFireConfigStore } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');
    if (!organizationId) {
      return;
    }

    form.setLoading(true);

    await userFireConfigStore.updateUserFireConfig(organizationId, {
      service: form.values.service
    });

    form.setLoading(false);
  };

  handleTargetGroupSubmit = async () => {
    const { form, userFireConfigStore } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');

    if (!organizationId) {
      return;
    }

    form.setLoading(true);
    await userFireConfigStore.updateUserFireConfig(organizationId, {
      target_group: form.values.target_group
    });
    form.setLoading(false);
  };

  handleBusinessTargetSubmit = async () => {
    const { form, userFireConfigStore } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');

    if (!organizationId) {
      return;
    }

    form.setLoading(true);
    await userFireConfigStore.updateUserFireConfig(organizationId, {
      business_target: form.values.business_target
    });
    form.setLoading(false);
  };

  getFire = async () => {
    const { fireStore, dataStore, history, applicationStore, projectsStore } =
      this.props;
    const fireId = idFromMatch(this.props.match, 'fireId');
    const organizationId = idFromMatch(this.props.match, 'orgId');

    if (!fireId || !organizationId) {
      return;
    }

    try {
      await fireStore.getFire(fireId);
    } catch (error) {
      applicationStore.setFlashMessage(
        this.props.intl.formatMessage({ id: 'access denied' }),
        'error'
      );
      history.push(ROUTE_FIRE + '/' + organizationId);
    }

    if (fireStore.fire) {
      if (dataStore && !dataStore.gptRequestCounterList) {
        projectsStore.getGPTRequestCounter(
          organizationId,
          fireStore.fire?.project_id
        );
      }

      const { projectAiModulesStore } = this.props;
      const projectId = fireStore.fire?.project_id;
      if (projectId && organizationId) {
        projectAiModulesStore.getProjectAiModules(organizationId, projectId);
      }

      const hash = window.location.hash.replace('#', '');
      if (fireStore.fire?.allTasksDone && !hash) {
        this.setState({ activeTab: 'briefing' });
      }
    }
  };

  generateMorePainpoints = async (clusterId: number | undefined) => {
    const { painpointsStore, dataStore } = this.props;

    const organizationId = dataStore.organization?.id;
    const projectId = dataStore.project?.id;
    const fireId = dataStore.fire?.id;

    if (!organizationId || !projectId || !clusterId || !fireId) {
      return;
    }

    await painpointsStore.generateMorePainpointsByAi(
      organizationId,
      projectId,
      clusterId,
      fireId
    );
  };

  handleCableUpdate(elementType: CableElementType, element: any) {
    const { dataStore } = this.props;

    switch (elementType) {
      case 'briefing':
        dataStore!.updateBriefingFromCable(element.id, element);
        break;

      case 'painpoint':
        dataStore!.updatePainpointFromCable(element.id, element);
        break;

      case 'cluster':
        dataStore!.updateClusterFromCable(element.id, element);
        break;

      case 'benchmark':
        dataStore!.updateBenchmarkFromCable(element.id, element);
        break;

      case 'hypothesis':
        dataStore!.updateHypothesisFromCable(element.id, element);
        break;

      case 'gptRequestCounter':
        dataStore!.updateGPTRequestCounterFromCable(element.id, element);

        if (
          element.request_type === 'briefingDetails' &&
          element.status === 'in_progress'
        ) {
          this.setState({
            briefingCompletionByGPTRequestCounter: element.current_amount || 0
          });
        } else if (
          element.request_type === 'briefingQuestion' &&
          element.status === 'completed'
        ) {
          this.setState({
            briefingCompletionByGPTRequestCounter:
              this.state.briefingCompletionByGPTRequestCounter + 1
          });
        }
        break;

      case 'fire':
        dataStore!.updateFireFromCable(element.id, element);
        break;

      case 'fire_task':
        dataStore!.updateFireTaskFromCable(element.id, element);
        break;

      case 'project_ai_module':
        this.props.projectAiModulesStore.handleProjectAiModuleUpdate(element);
        if (element.status === 'done') {
          this.handleTabChange(`ai_module_${element.id}`);
        }
        break;

      default:
    }
  }

  checkChangedPainpoint(element: PainpointModelType, newChecked: boolean) {
    const { dataStore } = this.props;

    const organizationId = idFromMatch(this.props.match, 'orgId');
    const projectId = dataStore.project?.id;

    if (!organizationId || !projectId) {
      return;
    }

    this.props.actionsStore.selectionSet(
      {
        Painpoint: [
          {
            id: element.id,
            organizationId,
            projectId,
            publishState: element.publish_state
          }
        ]
      },
      newChecked
    );
  }

  checkChangedBenchmark(element: BenchmarkModelType, newChecked: boolean) {
    const { dataStore } = this.props;

    const organizationId = idFromMatch(this.props.match, 'orgId');
    const projectId = dataStore.project?.id;

    if (!organizationId || !projectId) {
      return;
    }

    this.props.actionsStore.selectionSet(
      {
        Benchmark: [
          {
            id: element.id,
            organizationId,
            projectId,
            publishState: element.publish_state
          }
        ]
      },
      newChecked
    );
  }

  handleProjectAiModuleUpdate = async (
    projectAiModuleId: number,
    output: string
  ) => {
    const { projectAiModulesStore, dataStore, form } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');
    const projectId = dataStore.project?.id;

    if (!organizationId || !projectId) {
      return;
    }

    try {
      await projectAiModulesStore.updateProjectAiModule(
        organizationId,
        projectId,
        projectAiModuleId,
        output
      );
      form.setEditing();
    } catch (error) {
      handleFormError(form, error);
    }
  };

  handleBriefingFieldUpdate = async (field: string, value: string) => {
    const { dataStore, briefingStore } = this.props;
    if (!dataStore.briefing) {
      return;
    }
    const patch: any = {
      [field]: value
    };

    try {
      await briefingStore.createOrUpdateBriefing(patch);

      if (briefingStore.isItemSaveError) {
        this.renderError();
        return;
      }
      this.props.form.setEditing();
    } catch (error) {
      handleFormError(this.props.form, error);
    }
  };

  generateCluster = async () => {
    const { painpointsStore, dataStore } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');
    const projectId = dataStore.project?.id;
    const fireId = dataStore.fire?.id;

    if (!organizationId || !projectId || !fireId) {
      return;
    }

    try {
      await painpointsStore.generateSingleAICluster(
        organizationId,
        projectId,
        fireId
      );
    } catch (error: any) {
      this.renderError();
    }
  };

  toggleHypothesisById = (hypothesisId: number, newChecked: boolean) => {
    const { dataStore } = this.props;
    const hypothesis = dataStore.hypotheses?.get(String(hypothesisId));
    if (hypothesis) {
      this.checkChangedHypothesis(hypothesis, newChecked);
    }
  };

  checkChangedHypothesis(element: HypothesisModelType, newChecked: boolean) {
    const { dataStore } = this.props;

    const organizationId = idFromMatch(this.props.match, 'orgId');
    const projectId = dataStore.project?.id;

    if (!organizationId || !projectId) {
      return;
    }

    this.props.actionsStore.selectionSet(
      {
        Hypothesis: [
          {
            id: element.id,
            organizationId,
            projectId,
            publishState: element.publish_state
          }
        ]
      },
      newChecked
    );
  }

  convertFireToProject = async () => {
    const { fireStore, dataStore, history } = this.props;
    const { fire } = dataStore;
    const fireId = idFromMatch(this.props.match, 'fireId');
    const organizationId = idFromMatch(this.props.match, 'orgId');

    if (!fireId || !fire || !organizationId || !fire.project_id) {
      return;
    }

    await fireStore.convertFireToProject(fireId);

    history.push(
      ROUTE_PROJECT.replace(':orgId', organizationId.toString()).replace(
        ':projectId',
        fire.project_id.toString()
      )
    );
  };

  retryTasks = async () => {
    const { fireStore } = this.props;
    if (!fireStore.fire) {
      return;
    }

    await fireStore.retryTask(fireStore.fire.id);
  };

  downloadPowerpoint = async () => {
    const { actionsStore, dataStore } = this.props;
    const projectId = dataStore.project?.id;
    const fireId = dataStore.fire?.id;

    if (!projectId) {
      return;
    }

    this.setState({ isPowerpointLoading: true });
    await actionsStore.downloadPowerpoint(
      projectId,
      actionsStore.selectedIds,
      fireId
    );
    this.setState({ isPowerpointLoading: false });
  };

  downloadFullPowerpoint = async () => {
    const { actionsStore, dataStore } = this.props;
    const projectId = dataStore.project?.id;
    const fireId = dataStore.fire?.id;

    if (!projectId) {
      return;
    }

    this.setState({ isPowerpointLoading: true });
    await actionsStore.downloadPowerpointProject(projectId, fireId);
    this.setState({ isPowerpointLoading: false });
  };

  deleteFirePowerpoint = async () => {
    const { fireStore, dataStore } = this.props;
    const fireId = dataStore.fire?.id;
    const projectId = dataStore.project?.id;

    if (!fireId || !projectId) {
      return;
    }

    await fireStore.deleteFirePowerpoint(fireId, projectId);
  };

  handleTabChange = (tab: string) => {
    this.setState({ activeTab: tab });
    window.history.replaceState(null, '', `#${tab}`);
  };

  renderPage(content: React.ReactNode, showSidebar: boolean = true) {
    const { aiModulesStore, projectAiModulesStore, dataStore } = this.props;
    const organizationId = idFromMatch(this.props.match, 'orgId');
    const projectId = dataStore.project?.id;
    const hypotheses = dataStore.hypotheses;
    const fireStrategy = dataStore.fire?.fire_strategy;
    const powerpointUrl = dataStore.fire?.powerpoint_url;

    if (!organizationId) {
      return null;
    }

    const hypothesesList = hypotheses ? Array.from(hypotheses.values()) : [];

    return (
      <AppLayoutReduced active="fire">
        <Columns columnTemplate="1fr 22.1875rem" gap={GapSizes.NONE}>
          <main>
            <div className="fire-layout-content fire-layout-content--detail">
              {content}
            </div>
          </main>

          {showSidebar && (
            <AiModulesSidebarComponent
              aiModulesStore={aiModulesStore}
              projectAiModulesStore={projectAiModulesStore}
              hypotheses={hypothesesList}
              selectedHypothesisIds={this.props.actionsStore.selectedIds.Hypothesis}
              onHypothesisToggle={this.toggleHypothesisById}
              organizationId={organizationId}
              projectId={projectId}
              fireStrategy={fireStrategy}
              onDownloadPowerpoint={() => this.downloadFullPowerpoint()}
              onDeletePowerpoint={() => this.deleteFirePowerpoint()}
              onCopyToProject={() => this.convertFireToProject()}
              isPowerpointLoading={this.state.isPowerpointLoading}
              powerpointUrl={powerpointUrl}
              tasksRunning={
                dataStore.fire ? !dataStore.fire.allTasksDone : false
              }
            />
          )}
        </Columns>
      </AppLayoutReduced>
    );
  }

  renderLoading() {
    return this.renderPage(
      <>
        <Loading />
      </>,
      false
    );
  }

  renderError() {
    return this.renderPage(
      <>
        <ErrorMessage
          state={this.props.fireStore.itemLoadingState}
          onRetry={() => this.getFire()}
        />
      </>,
      false
    );
  }

  renderSettingsOverlay() {
    const organizationId = idFromMatch(this.props.match, 'orgId');
    if (!organizationId) {
      return null;
    }

    return (
      <UserFireConfigBenchmarkSettings
        organizationId={organizationId}
        userFireConfig={this.props.userFireConfigStore.userFireConfig}
        userFireConfigStore={this.props.userFireConfigStore}
        setShowSettings={() => this.setState({ showSettingsOverlay: false })}
        onSubmit={() => this.setState({ showSettingsOverlay: false })}
        onAbort={() => this.setState({ showSettingsOverlay: false })}
      />
    );
  }

  renderButtonTitle(tab: string, count?: number) {
    const { intl } = this.props;

    switch (tab) {
      case 'briefing':
        return (
          <div
            className="fire-detail-screen__tablist-button-title"
            title={intl.formatMessage({ id: 'Briefing' })}
          >
            <Icon name="envelope" />
            <span>
              <FormattedMessage id="Briefing" />
            </span>
          </div>
        );
      case 'painpoints':
        return (
          <div
            className="fire-detail-screen__tablist-button-title"
            title={`${intl.formatMessage({ id: 'Painpoints' })} (${count || 0})`}
          >
            <Icon name="clipboard" />
            <span>
              <FormattedMessage id="Painpoints" />
              <span className="tablist__button-count"> ({count || 0})</span>
            </span>
          </div>
        );
      case 'benchmarks':
        return (
          <div
            className="fire-detail-screen__tablist-button-title"
            title={`${intl.formatMessage({ id: 'Benchmarks' })} (${count || 0})`}
          >
            <Icon name="detail" />
            <span>
              <FormattedMessage id="Benchmarks" />
              <span className="tablist__button-count"> ({count || 0})</span>
            </span>
          </div>
        );
      case 'hypothesen':
        return (
          <div
            className="fire-detail-screen__tablist-button-title"
            title={`${intl.formatMessage({ id: 'Hypotheses' })} (${count || 0})`}
          >
            <Icon name="bulb" />
            <span>
              <FormattedMessage id="Hypotheses" />
              <span className="tablist__button-count"> ({count || 0})</span>
            </span>
          </div>
        );
      default:
        return '';
    }
  }

  render() {
    const { fireStore, dataStore, form, actionsStore } = this.props;
    const {
      fire,
      briefing,
      benchmarks,
      painpoints,
      hypotheses,
      user,
      customers,
      currentOrganization
    } = dataStore;
    const organizationId = idFromMatch(this.props.match, 'orgId');
    const projectId = dataStore.project?.id;

    if (fireStore.isItemLoading || form.loading) {
      return this.renderLoading();
    }

    if (fireStore.isListError) {
      return this.renderError();
    }

    if (!fire || !user || !user.org_memberships) {
      return this.renderError();
    }

    if (!organizationId || !projectId || !currentOrganization) {
      return null;
    }

    const editable = fire.isEditable(user.id);

    const painpointsGroupedByCluster: ClusterPainpoints[] = [];

    painpoints?.forEach((painpoint) => {
      const existingCluster = painpointsGroupedByCluster.find(
        (cluster) => cluster.cluster?.id === painpoint.cluster?.id
      );
      if (existingCluster) {
        existingCluster.painpoints.push(painpoint);
      } else {
        painpointsGroupedByCluster.push({
          cluster: painpoint.cluster,
          painpoints: [painpoint]
        });
      }
    });

    const benchmarksList = Array.from(benchmarks?.values() || [])
      .sort((a, b) => a.id - b.id)
      .reverse();
    const hypothesisList = Array.from(hypotheses?.values() || [])
      .sort((a, b) => a.id - b.id)
      .reverse();

    const { projectAiModulesStore } = this.props;
    const projectAiModuleList = projectAiModulesStore.projectAiModuleList;

    const organizations: { id: number; name: string }[] = [];

    // QUICK FIX: Customer Workshop
    if (user.role === ROLES.ADMIN) {
      organizations.push(...user.sortedOrganizationList);
    } else {
      const customerList = Array.from(customers.values()).map((customer) => ({
        id: customer.id,
        name: customer.name || ''
      }));

      organizations.push(...customerList);

      if (!currentOrganization.is_agency) {
        const agency_id = currentOrganization.agency_id;
        if (agency_id) {
          organizations.push({
            id: agency_id,
            name: currentOrganization.agency_name || ''
          });
        } else {
          organizations.push({
            id: currentOrganization.id,
            name: currentOrganization.name || ''
          });
        }
      } else {
        organizations.push({
          id: currentOrganization.id,
          name: currentOrganization.name || ''
        });
      }
    }
    const fireTasks = fire.sortedFireTasks;

    const briefingTask = fireTasks.find(
      (task) => task.task_type === 'generate_briefing'
    );
    const painpointsTask = fireTasks.find(
      (task) => task.task_type === 'generate_painpoints'
    );
    const benchmarksTask = fireTasks.find(
      (task) => task.task_type === 'generate_benchmarks'
    );
    const hypothesesTask = fireTasks.find(
      (task) => task.task_type === 'generate_hypotheses'
    );

    const clusterNames: { id: number; name: string }[] = [];

    painpoints?.forEach((painpoint: PainpointModelType) => {
      const existingCluster = clusterNames.find(
        (cluster) => cluster.id === painpoint.cluster?.id
      );
      if (existingCluster) {
        existingCluster.name = painpoint.cluster?.name || '';
      } else {
        clusterNames.push({
          id: painpoint.cluster?.id || 0,
          name: painpoint.cluster?.name || ''
        });
      }
    });

    const checked = actionsStore.selectedIds;

    const allTasksDone = fire.allTasksDone;

    const notDeleting = actionsStore.deleteLoadingState !== 'loading';

    const showSidebar = !projectAiModulesStore.isListLoading;

    return this.renderPage(
      <div className="l-item-stack-regular">
        <div className="page-header page-header--reduced page-header--fire">
          <h1 className="page-header__title">
            <svg aria-hidden="true" focusable="false">
              <use
                xlinkHref={'/headlines/headline-symbols.svg#' + 'Assistent'}
                width="100%"
              />
            </svg>
            <span className="sr-only">Assitent</span>
          </h1>
        </div>

        <div
          className="l-item-stack-regular"
          style={{
            marginTop: '-10em',
            zIndex: '10'
          }}
        >
          {dataStore.project?.ai_at_work && <AiLoading />}

          <FireHeaderComponent
            currentOrganizationId={organizationId}
            organizations={organizations}
            isDetailView={true}
            editable={editable}
            switchOrganization={(orgId) => {
              this.props.history.push(ROUTE_FIRE + '/' + orgId);
            }}
            setShowSettings={() => this.setState({ showSettingsOverlay: true })}
          />

          <div className="fire-detail-screen fire-layout-content__detail-container">
            <FireDetailTop fire={fire} user={user} />

            <div className="fire-detail-screen__project-container">
              <div className="fire-detail-screen__project-name">
                <h2>{dataStore.project?.topic}</h2>
              </div>
            </div>

            {(() => {
              const MAX_VISIBLE_TABS = 5;
              const allTabs: OverflowTab[] = [];

              if (
                briefingTask?.status === 'done' ||
                this.state.briefingCompletionByGPTRequestCounter === 6
              ) {
                allTabs.push({
                  key: 'briefing',
                  title: this.renderButtonTitle('briefing'),
                  tabId: 'briefing'
                });
              }
              if (
                briefingTask?.status === 'done' ||
                painpointsGroupedByCluster.length > 0
              ) {
                allTabs.push({
                  key: 'painpoints',
                  title: this.renderButtonTitle('painpoints', painpoints?.size),
                  tabId: 'painpoints'
                });
              }
              if (
                painpointsTask?.status === 'done' &&
                benchmarksTask !== undefined &&
                benchmarksList.length > 0
              ) {
                allTabs.push({
                  key: 'benchmarks',
                  title: this.renderButtonTitle(
                    'benchmarks',
                    benchmarksList.length
                  ),
                  tabId: 'benchmarks'
                });
              }
              if (
                benchmarksTask?.status === 'done' &&
                hypothesesTask !== undefined &&
                hypothesisList.length > 0
              ) {
                allTabs.push({
                  key: 'hypothesen',
                  title: this.renderButtonTitle(
                    'hypothesen',
                    hypothesisList.length
                  ),
                  tabId: 'hypothesen'
                });
              }

              projectAiModuleList.forEach((pam) => {
                const aiModuleName = pam.ai_module?.name || 'AI Module';
                allTabs.push({
                  key: `ai_module_${pam.id}`,
                  title: (
                    <div
                      className="fire-detail-screen__tablist-button-title"
                      title={aiModuleName}
                    >
                      {pam.ai_module?.icon_url ? (
                        <img
                          src={pam.ai_module.icon_url}
                          alt={aiModuleName}
                          className="ai-module-card__icon"
                        />
                      ) : (
                        <Icon name="ai" />
                      )}
                      <span>{aiModuleName}</span>
                    </div>
                  ),
                  tabId: `ai_module_${pam.id}`
                });
              });

              if (!allTasksDone) {
                allTabs.push({
                  key: 'progress',
                  title: <FormattedMessage id="fire progress" />,
                  tabId: 'progress'
                });
              }

              let mainTabs = allTabs.slice(0, MAX_VISIBLE_TABS);
              let overflowTabs = allTabs.slice(MAX_VISIBLE_TABS);

              const activeInOverflow = overflowTabs.findIndex(
                (t) => t.tabId === this.state.activeTab
              );
              if (activeInOverflow !== -1 && mainTabs.length > 0) {
                const swapped = mainTabs[mainTabs.length - 1];
                mainTabs = [
                  ...mainTabs.slice(0, -1),
                  overflowTabs[activeInOverflow]
                ];
                overflowTabs = [
                  swapped,
                  ...overflowTabs.slice(0, activeInOverflow),
                  ...overflowTabs.slice(activeInOverflow + 1)
                ];
              }

              return (
                <TabList border={true} extraSmall={true}>
                  {mainTabs.map((tab) => (
                    <TabListButton
                      key={tab.key}
                      title={tab.title}
                      to={''}
                      active={this.state.activeTab === tab.tabId}
                      onClick={() => this.handleTabChange(tab.tabId)}
                    />
                  ))}
                  {overflowTabs.length > 0 && (
                    <TabListMore
                      label={<FormattedMessage id="fire tabs more" />}
                      tabs={overflowTabs}
                      activeTab={this.state.activeTab}
                      onTabChange={this.handleTabChange}
                    />
                  )}
                </TabList>
              );
            })()}

            {this.state.activeTab === 'progress' && (
              <div>
                <FireProgressComponent
                  stepsAndStatus={
                    fire.fire_type === 'ideation'
                      ? [
                          {
                            step: 'briefing',
                            status: briefingTask?.status,
                            current_completion:
                              briefingTask?.status === 'done'
                                ? 6
                                : this.state
                                    .briefingCompletionByGPTRequestCounter,
                            target_completion: 6
                          },
                          {
                            step: 'painpoints',
                            status: painpointsTask?.status,
                            current_completion:
                              painpointsGroupedByCluster.length,
                            target_completion: fire.inital_cluster_amount
                          },
                          {
                            step: 'benchmarks',
                            status: benchmarksTask?.status,
                            current_completion: benchmarksList.length,
                            target_completion: fire.inital_benchmark_amount
                          },
                          {
                            step: 'hypotheses',
                            status: hypothesesTask?.status,
                            current_completion: hypothesisList.length,
                            target_completion: fire.inital_hypothesis_amount
                          }
                        ]
                      : fire.fire_type === 'benchmarking'
                        ? [
                            {
                              step: 'briefing',
                              status: briefingTask?.status,
                              current_completion:
                                briefingTask?.status === 'done'
                                  ? 6
                                  : this.state
                                      .briefingCompletionByGPTRequestCounter,
                              target_completion: 6
                            },
                            {
                              step: 'painpoints',
                              status: painpointsTask?.status,
                              current_completion:
                                painpointsGroupedByCluster.length,
                              target_completion: fire.inital_cluster_amount
                            },
                            {
                              step: 'benchmarks',
                              status: benchmarksTask?.status,
                              current_completion: benchmarksList.length,
                              target_completion: fire.inital_benchmark_amount
                            }
                          ]
                        : fire.fire_type === 'brainstorming'
                          ? [
                              {
                                step: 'briefing',
                                status: briefingTask?.status,
                                current_completion:
                                  briefingTask?.status === 'done'
                                    ? 6
                                    : this.state
                                        .briefingCompletionByGPTRequestCounter,
                                target_completion: 6
                              },
                              {
                                step: 'painpoints',
                                status: painpointsTask?.status,
                                current_completion:
                                  painpointsGroupedByCluster.length,
                                target_completion: fire.inital_cluster_amount
                              }
                            ]
                          : []
                  }
                  retryTasks={() => {
                    this.retryTasks();
                  }}
                />
              </div>
            )}

            {this.state.activeTab === 'briefing' && (
              <>
                <h2 className="fire-detail-screen__title">
                  <FormattedMessage id="Fire briefing info" />
                </h2>

                <FireBriefingComponent
                  briefing={briefing || fire.briefing}
                  handleBriefingFieldUpdate={(field, value) => {
                    this.handleBriefingFieldUpdate(field, value);
                  }}
                  form={form}
                />
              </>
            )}

            {this.state.activeTab === 'painpoints' && (
              <div>
                <h2 className="fire-detail-screen__title">
                  <FormattedMessage id="Fire painpoint info" />
                </h2>

                {painpointsGroupedByCluster.map(
                  (clusterPainpoint) =>
                    clusterPainpoint.cluster && (
                      <ClusterWithPainpointComponent
                        key={clusterPainpoint.cluster.id}
                        cluster={clusterPainpoint.cluster}
                        clusters={clusterNames}
                        painpoints={clusterPainpoint.painpoints}
                        onAiButtonClick={() => {
                          this.generateMorePainpoints(
                            clusterPainpoint.cluster?.id
                          );
                        }}
                        onAddPainpointClick={(newPainpoint) => {
                          this.addPainpoint(newPainpoint);
                        }}
                        organizationId={organizationId}
                        projectId={projectId}
                        handlePainpointChange={(painpoint, checked) => {
                          this.checkChangedPainpoint(painpoint, checked);
                        }}
                        editable={editable}
                        checked={checked.Painpoint}
                        fireId={fire.id}
                      />
                    )
                )}

                {editable && (
                  <div className="fire-detail-screen__add-cluster-button">
                    <MainButton
                      onClick={() => {
                        this.generateCluster();
                      }}
                    >
                      <FormattedMessage id="fire add cluster" />
                    </MainButton>
                  </div>
                )}
              </div>
            )}

            {this.state.activeTab === 'benchmarks' && (
              <div>
                <h2 className="fire-detail-screen__title">
                  <FormattedMessage id="Fire benchmark info" />
                </h2>

                <ColumnWrapper gap="3em">
                  {benchmarksList.map((benchmark) => {
                    if (editable) {
                      return (
                        <BenchmarksListItem
                          key={benchmark.id}
                          benchmark={benchmark}
                          contextUri={`/app/lab/${organizationId}/${projectId}`}
                          onChange={(checked) => {
                            this.checkChangedBenchmark(benchmark, checked);
                          }}
                          fromList={true}
                          checked={checked.Benchmark.indexOf(benchmark.id) > -1}
                          fireBackRoute={
                            ROUTE_FIRE + '/' + organizationId + '/' + fire.id
                          }
                        />
                      );
                    }
                    return (
                      <BenchmarksListItem
                        key={benchmark.id}
                        benchmark={benchmark}
                        contextUri={`/app/lab/${organizationId}/${projectId}`}
                        fromList={true}
                      />
                    );
                  })}
                </ColumnWrapper>
              </div>
            )}

            {this.state.activeTab === 'hypothesen' && (
              <div>
                <h2 className="fire-detail-screen__title">
                  <FormattedMessage id="Fire hypothesis info" />
                </h2>

                <ColumnWrapper gap="3em">
                  {hypothesisList.map((hypothesis) => {
                    if (editable) {
                      return (
                        <HypothesesListItem
                          key={hypothesis.id}
                          hypothesis={hypothesis}
                          contextUri={`/app/lab/${organizationId}/${projectId}`}
                          onChange={(checked) => {
                            this.checkChangedHypothesis(hypothesis, checked);
                          }}
                          fromList={true}
                          checked={
                            checked.Hypothesis.indexOf(hypothesis.id) > -1
                          }
                          fireBackRoute={
                            ROUTE_FIRE +
                            '/' +
                            organizationId +
                            '/' +
                            fire.id +
                            '#hypothesen'
                          }
                        />
                      );
                    }
                    return (
                      <HypothesesListItem
                        key={hypothesis.id}
                        hypothesis={hypothesis}
                        contextUri={`/app/lab/${organizationId}/${projectId}`}
                        fromList={true}
                      />
                    );
                  })}
                </ColumnWrapper>
              </div>
            )}

            {projectAiModuleList.map((pam) =>
              this.state.activeTab === `ai_module_${pam.id}` ? (
                <div key={pam.id}>
                  <ProjectAiModuleContent
                    projectAiModule={pam}
                    form={form}
                    onSave={(projectAiModuleId, output) => {
                      this.handleProjectAiModuleUpdate(
                        projectAiModuleId,
                        output
                      );
                    }}
                  />
                </div>
              ) : null
            )}

            {projectId && (
              <ActionCableComponent
                channel="ProjectChannel"
                projectId={projectId}
                onUpdated={(elementType, element) =>
                  this.handleCableUpdate(elementType, element)
                }
              />
            )}

            {this.state.showSettingsOverlay &&
              createPortal(
                this.renderSettingsOverlay(),
                document.body
              )}
            {editable && notDeleting &&
              createPortal(
                <ActionsOverlayContainer
                  copyInitiallySelectedOrganizationId={organizationId}
                  copyInitiallySelectedProjectId={projectId}
                  showCopy={false}
                  showAiBenchmark={true}
                  showAiBriefing={false}
                  showDelete={true}
                  showShare={true}
                  fireContext={true}
                  disableShareLink={true}
                  disableCreatePrototypes={true}
                />,
                document.body
              )}
          </div>
        </div>
      </div>,
      showSidebar
    );
  }
}

export default injectIntl(
  (props: FireDetailScreenProps & HistoryProps & WrappedComponentProps) => {
    const form = useForm();
    return <FireDetailScreen {...props} form={form} />;
  }
);
